import CloseIcon from '@mui/icons-material/Close';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    Typography,
} from '@mui/material';
import React from 'react';
import { ListItems } from '../../../layout/styledcomponents/TooltipStyling';
import { capitalizeName } from '../../../shared/utils';
import { ModalProps } from '../../types/modal';
import { BasePolicy } from '../../types/policy';

interface Props extends ModalProps {
    policy: Partial<BasePolicy>;
    cancellationPolicyDetails: {
        title: string;
        value: string;
        items: string[];
    };
}

const PolicyModal = ({
    policy,
    open,
    onClose,
    cancellationPolicyDetails,
}: Props) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                {policy?.name
                    ? capitalizeName(policy.name)
                    : 'Cancellation Policy'}
                <IconButton
                    onClick={onClose}
                    size='small'
                    aria-label='close'
                    sx={{ color: 'text.primary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontWeight: 'bold', mb: 1 }} variant='body1'>
                    Cancellation Policy:{' '}
                    {capitalizeName(
                        policy?.cancellationType === 'nonRefundable'
                            ? 'Non-Refundable'
                            : policy?.cancellationType,
                    )}
                </Typography>
                {cancellationPolicyDetails && (
                    <List
                        dense
                        sx={{
                            paddingLeft: 3,
                            py: 0,
                            mb: policy?.description ? 2 : 0,
                            '& .MuiListItem-root': {
                                py: 0.2,
                            },
                        }}
                    >
                        {cancellationPolicyDetails.items.map((text, index) => (
                            <ListItems key={index} text={text} />
                        ))}
                    </List>
                )}
                <Typography
                    component='div'
                    sx={{
                        fontSize: '12px',
                        color: 'text.primary',
                        whiteSpace: 'pre-wrap',
                        textAlign: 'left',
                        '& p': { margin: 0 },
                    }}
                >
                    {policy?.description?.split('\n').map((line, index) => {
                        const trimmed = line.trim();

                        // Only consider lines short enough to be headings, not paragraphs
                        const isHeading =
                            trimmed &&
                            trimmed.length < 60 && // avoid bolding long sentences like the intro
                            /^[A-Z][A-Za-z\s\-&/]+$/.test(
                                trimmed.replace(/:$/, ''),
                            ); // title-like text

                        return (
                            <Typography
                                key={index}
                                component='p'
                                variant='body1'
                                sx={{
                                    fontWeight: isHeading ? 'bold' : 'normal',
                                    paddingTop: isHeading ? 1 : 0,
                                    paddingBottom: isHeading ? 1 : 0,
                                    lineHeight: 1.6,
                                }}
                            >
                                {line}
                            </Typography>
                        );
                    })}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PolicyModal;
